// LSM V2 add-on rows (canvas Product board, F21).
// Easify Product Options draws each add-on as a Yes/No dropdown. This draws the canvas
// checkbox rows in the add-ons slot and drives those dropdowns, so Easify still prices and
// adds the add-ons. Easify's own UI is hidden only once every option has been read as a
// plain "No / Yes, add ... ($price)" pair; anything else leaves Easify's UI as it is.
// Also keeps the "Total with add-ons" line and the "ships free" sentence current.

const PRICE_RE = /\(\$([\d,]+(?:\.\d{1,2})?)\)\s*$/;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const clean = (text) => text.replace(/✓/g, '').replace(/\s+/g, ' ').trim();
const commas = (text) => text.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const money = (cents) => {
  const [whole, part] = (cents / 100).toFixed(2).split('.');
  return `$${commas(whole)}.${part}`;
};
const plusMoney = (cents) => (cents % 100 === 0 ? `+$${commas(String(cents / 100))}` : `+${money(cents)}`);

function press(element) {
  for (const type of ['pointerdown', 'mousedown', 'pointerup', 'mouseup']) {
    element.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window }));
  }
  element.click();
}

async function listItems(option) {
  const button = option.querySelector('.po-select-button');
  if (!button) return null;
  const wasOpen = button.getAttribute('aria-expanded') === 'true';
  if (!wasOpen) press(button);
  let items = [];
  for (let i = 0; i < 10 && !items.length; i++) {
    await wait(30);
    items = [...option.querySelectorAll('.po-select-listItem')];
  }
  const texts = items.map((item) => clean(item.textContent));
  if (!wasOpen && button.getAttribute('aria-expanded') === 'true') press(button);
  return texts;
}

async function choose(option, index) {
  const button = option.querySelector('.po-select-button');
  if (!button) return false;
  if (button.getAttribute('aria-expanded') !== 'true') press(button);
  let item = null;
  for (let i = 0; i < 10 && !item; i++) {
    await wait(30);
    item = option.querySelectorAll('.po-select-listItem')[index] || null;
  }
  if (!item) return false;
  press(item);
  await wait(60);
  if (button.getAttribute('aria-expanded') === 'true') press(button);
  return true;
}

const isYes = (value) => /^yes\b/i.test(value || '');

class LsmAddons {
  constructor(slot) {
    this.slot = slot;
    this.details = slot.closest('.product-details') || slot.closest('[id*="ProductInformation-"]') || document;
    this.section = slot.closest('[id*="ProductInformation-"]') || document;
    this.buyline = this.details.querySelector('[data-lsm-buyline]') || document.querySelector('[data-lsm-buyline]');
    this.rows = [];
    this.copy = this.readCopy();
    this.busy = false;
    this.section.addEventListener('variant:update', (event) => this.onVariant(event));
    this.details.addEventListener('change', () => this.updateTotal());
    this.details.addEventListener('click', (event) => {
      if (event.target.closest('quantity-selector-component, .quantity-selector')) setTimeout(() => this.updateTotal(), 50);
    });
    this.watchForEasify();
    this.updateTotal();
  }

  readCopy() {
    const map = {};
    const script = this.slot.querySelector('[data-lsm-addon-copy]');
    if (!script) return map;
    try {
      for (const line of JSON.parse(script.textContent)) {
        const [label, rest] = line.split('=');
        if (!rest) continue;
        const [title, description] = rest.split('|');
        map[label.trim().toLowerCase()] = { title: (title || '').trim(), description: (description || '').trim() };
      }
    } catch (error) {
      return map;
    }
    return map;
  }

  easify() {
    return this.details.querySelector('.easify-product-options') || document.querySelector('.easify-product-options');
  }

  // Easify can take several seconds to mount. While we wait and read its options, its UI
  // stays hidden (lsm-addons-pending) so the dropdowns do not flash; any failure shows it.
  // Options without a dropdown (text fields such as an order number) are shown after a short
  // grace, since there is nothing to redraw.
  watchForEasify() {
    document.documentElement.classList.add('lsm-addons-pending');
    let tries = 0;
    let grace = 0;
    const attempt = () => {
      const root = this.easify();
      const mounted = root ? root.querySelectorAll('.po-option-root').length > 0 : false;
      if (mounted && root.querySelector('.po-option-root .po-select-button')) {
        this.build(root).then((built) => {
          if (!built) this.release();
        }, () => this.release());
      } else if (mounted && grace++ < 6) {
        setTimeout(attempt, 250);
      } else if (mounted) {
        this.release();
      } else if (tries++ < 120) {
        setTimeout(attempt, 250);
      } else {
        this.release();
      }
    };
    attempt();
  }

  release() {
    document.documentElement.classList.remove('lsm-addons-pending');
    this.nameEasifyFields();
  }

  // Easify's labels are not tied to its inputs, so screen readers hear an unnamed field.
  nameEasifyFields() {
    const root = this.easify();
    if (!root) return;
    for (const option of root.querySelectorAll('.po-option-root')) {
      const label = clean(option.querySelector('.po-label-label')?.textContent || '').replace(/\*$/, '').trim();
      if (!label) continue;
      for (const field of option.querySelectorAll('input:not([type="hidden"]), textarea, select')) {
        if (!field.getAttribute('aria-label') && !field.labels?.length) field.setAttribute('aria-label', label);
      }
    }
  }

  async build(root) {
    const options = [...root.querySelectorAll('.po-option-root')];
    const specs = [];
    for (const option of options) {
      const input = option.querySelector('input[type="hidden"][name^="properties["]');
      const label = clean(option.querySelector('.po-label-label')?.textContent || '').replace(/\*$/, '').trim();
      const items = await listItems(option);
      if (!input || !items || items.length !== 2) return false;
      const yesIndex = items.findIndex((text) => isYes(text) && PRICE_RE.test(text));
      const noIndex = yesIndex === 0 ? 1 : 0;
      if (yesIndex < 0 || isYes(items[noIndex]) || PRICE_RE.test(items[noIndex])) return false;
      const yes = items[yesIndex];
      const cents = Math.round(parseFloat(yes.match(PRICE_RE)[1].replace(/,/g, '')) * 100);
      const copy = this.copy[label.toLowerCase()] || {};
      let title = copy.title;
      if (!title) {
        title = yes.replace(/^yes,?\s*(add\s+)?(the\s+)?/i, '').replace(/\s*\([^)]*\)/g, '').trim();
        title = title.charAt(0).toUpperCase() + title.slice(1);
      }
      specs.push({ id: option.dataset.optionId || '', option, input, label, yesIndex, noIndex, cents, title, description: copy.description || '' });
    }
    if (!specs.length) return false;
    this.render(specs);
    root.classList.add('lsm-easify-proxied');
    this.release();
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'style', 'class'] });
    this.sync();
    return true;
  }

  render(specs) {
    const wrap = document.createElement('div');
    wrap.className = 'lsm-addons';
    wrap.innerHTML = '<h2 class="lsm-addons__title">Add-ons</h2>';
    const note = this.slot.querySelector('.lsm-pdp-note');
    let noteAfter = specs.find((spec) => /extension/i.test(`${spec.label} ${spec.title}`)) || specs[0];
    this.rows = specs.map((spec, index) => {
      const row = document.createElement('label');
      row.className = 'lsm-addon';
      const id = `LsmAddon-${index}`;
      row.innerHTML = `
        <span class="lsm-addon__box"><input type="checkbox" id="${id}"></span>
        <span class="lsm-addon__text"><span class="lsm-addon__name"></span><span class="lsm-addon__desc"></span></span>
        <span class="lsm-addon__price"></span>`;
      row.querySelector('.lsm-addon__name').textContent = spec.title;
      const desc = row.querySelector('.lsm-addon__desc');
      if (spec.description) desc.textContent = spec.description;
      else desc.remove();
      row.querySelector('.lsm-addon__price').textContent = plusMoney(spec.cents);
      const checkbox = row.querySelector('input');
      checkbox.addEventListener('change', () => this.toggle(spec, checkbox));
      wrap.appendChild(row);
      if (note && spec === noteAfter) {
        const holder = document.createElement('div');
        holder.className = 'lsm-addons__note';
        holder.appendChild(note);
        wrap.appendChild(holder);
      }
      return { spec, row, checkbox };
    });
    const end = document.createElement('div');
    end.className = 'lsm-addons__end';
    wrap.appendChild(end);
    this.slot.prepend(wrap);
    this.slot.classList.add('lsm-addons-slot--built');
  }

  // Easify may re-render; find the option again by its id before using it.
  refresh(spec) {
    const root = this.easify();
    const fresh = spec.id && root ? root.querySelector(`.po-option-root[data-option-id="${spec.id}"]`) : null;
    if (fresh && fresh !== spec.option) {
      spec.option = fresh;
      spec.input = fresh.querySelector('input[type="hidden"][name^="properties["]') || spec.input;
    }
    return spec;
  }

  async toggle(spec, checkbox) {
    if (this.busy) return;
    this.refresh(spec);
    this.busy = true;
    const ok = await choose(spec.option, checkbox.checked ? spec.yesIndex : spec.noIndex);
    await wait(80);
    this.busy = false;
    if (!ok || isYes(spec.input.value) !== checkbox.checked) {
      this.fallback();
      return;
    }
    this.sync();
  }

  sync() {
    if (this.busy) return;
    for (const { spec, row, checkbox } of this.rows) {
      this.refresh(spec);
      const present = spec.option.isConnected && getComputedStyle(spec.option).display !== 'none';
      row.hidden = !present;
      if (row.nextElementSibling?.classList.contains('lsm-addons__note')) row.nextElementSibling.hidden = !present;
      checkbox.checked = present && isYes(spec.input.value);
    }
    this.updateTotal();
  }

  fallback() {
    this.release();
    this.easify()?.classList.remove('lsm-easify-proxied');
    this.observer?.disconnect();
    this.slot.querySelector('.lsm-addons')?.remove();
    this.rows = [];
    this.slot.classList.remove('lsm-addons-slot--built');
    this.updateTotal();
  }

  onVariant(event) {
    const price = event?.detail?.resource?.price;
    if (this.buyline && typeof price === 'number') this.buyline.dataset.price = String(price);
    setTimeout(() => this.sync(), 150);
  }

  updateTotal() {
    if (!this.buyline) return;
    const base = parseInt(this.buyline.dataset.price || '0', 10);
    const checked = this.rows.filter(({ row, checkbox }) => !row.hidden && checkbox.checked);
    const extra = checked.reduce((sum, { spec }) => sum + spec.cents, 0);
    const quantityInput = this.details.querySelector('input[name="quantity"]');
    const quantity = Math.max(1, parseInt(quantityInput?.value || '1', 10) || 1);
    const total = (base + extra) * quantity;
    const totalLine = this.buyline.querySelector('[data-lsm-total]');
    if (totalLine) {
      totalLine.hidden = checked.length === 0;
      totalLine.querySelector('[data-lsm-total-value]').textContent = money(total);
    }
    const free = this.buyline.querySelector('[data-lsm-ships-free]');
    if (free) free.hidden = total < 10000;
  }
}

document.querySelectorAll('[data-lsm-addons]').forEach((slot) => {
  if (!slot.lsmAddons) slot.lsmAddons = new LsmAddons(slot);
});
